/*eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as DeviceStatusIcon } from '../../assets/icons/dot-circle.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search-icon.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import {
  WebsocketListingProperties,
  WebsocketListingRequest,
} from '../../interface/blocked-neverblocked.interface';
import { DeviceListingProperties } from '../../interface/device-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchErrorLogs,
  fetchErrorLogsFilters,
  setSelectedDevice,
  setVlbcSearchBy,
  updateErrorLogsFilters,
} from '../../redux/slices/DeviceErrorLogs';
import CustomInput from '../../shared-components/custom-input/CustomInput';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import PageTitle from '../../shared-components/page-title/PageTitle';
import DeviceSidePanel from '../../shared-components/table-side-panel/DeviceSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertTimeStopToUTC from '../../shared-functions/ConvertTimeStopToUTC';

interface CheckBoxProperties {
  leftTopContent?: JSX.Element;
}

/**
 * Error logs component
 * @param properties
 * @returns
 */
function ErrorLogs(properties: CheckBoxProperties) {
  const { t } = useTranslation('devices');
  const navigate = useNavigate();
  const { leftTopContent } = properties;
  const [valueToShow, setValueToShow] = useState('');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useAppDispatch();
  const { devices, limit, page, loading, totalPages, totalResults, filters } = useAppSelector(
    (state) => state.deviceErrorLogs,
  );

  /**
   * UI of Status
   * @param device
   * @returns
   */
  const renderStatus = (device: WebsocketListingProperties) =>
    device?.isDeviceOffline !== undefined ? (
      <div>
        <div className={device.isDeviceOffline ? 'tag-danger-label' : 'tag-success-label'}>
          <DeviceStatusIcon />
          <span style={{ fontWeight: '700' }}>
            {device.isDeviceOffline ? t('offline') : t('online')}
          </span>
        </div>
      </div>
    ) : (
      <div />
    );

  /**
   * Function to open device view model
   */
  const handleOpenModelView = (chargingPoint: DeviceListingProperties, newTab: boolean) => {
    if (chargingPoint) {
      dispatch(
        setSelectedDevice({
          id: chargingPoint?.id,
          name: chargingPoint?.chargingPointName,
          chargingPointSn: chargingPoint?.chargingPointSn,
          modelSeries: chargingPoint?.modelSeries,
        }),
      );

      if (newTab) {
        const url = `/ev-admin/device-management/device/${chargingPoint.id}/device-details?searchBy=${chargingPoint.chargingPointName}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `/ev-admin/device-management/device/${chargingPoint.id}/device-details`,
          search: `?searchBy=${chargingPoint.chargingPointName}`,
        });
      }
    } else {
      console.log(t('erroropeningSeparatePage'));
    }
  };

  const column = [
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof WebsocketListingProperties,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
      handleClickExpandView: handleOpenModelView,
    },
    {
      label: t('deviceSN'),
      queryParamsKey: 'deviceId',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
      accessorKey: 'chargingPointSn' as keyof WebsocketListingProperties,
      sortAllow: false,
      handleClickExpandView: handleOpenModelView,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('deviceModel'),
      accessorKey: 'chargePointModel' as keyof WebsocketListingProperties,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('maxPowerOutput'),
      accessorKey: 'maximumOutputPower' as keyof WebsocketListingProperties,
      sortAllow: false,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: WebsocketListingProperties) => (
        <p>
          {currentRow.maximumOutputPower} {currentRow.maximumOutputPowerUnit}
        </p>
      ),
    },
    {
      label: t('deviceStatus'),
      accessorKey: 'isDeviceOffline' as keyof WebsocketListingProperties,
      customData: renderStatus,
      sortAllow: false,
    },
    {
      label: t('errorLogs'),
      accessorKey: 'errors' as keyof WebsocketListingProperties,
      columnWidth: 200,
      /**
       * Rendering timestamp and message
       * @param currentRow
       * @returns
       */
      customData: (currentRow: WebsocketListingProperties) => {
        return (
          <div className="error-logs">
            {currentRow.errors?.map((log, index) => {
              return <p key={index}>{`${ConvertTimeStopToUTC(log.timestamp)} - ${log.message}`}</p>;
            })}
          </div>
        );
      },
    },
  ];

  /**
   * Handles changes to the filter input and updates the search value.
   * @param name - The name of the filter.
   * @param value - The value to filter by.
   */
  const handleChangeFilters = (name: string, value: string) => {
    setValueToShow(value);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      dispatch(fetchErrorLogs({ searchBy: value }));
    }, 1000);
    dispatch(setVlbcSearchBy(value));
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: WebsocketListingRequest) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(fetchErrorLogs({ ...value, ...queryParameters }));
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(fetchErrorLogs({ ...queryParameters, page, limit }));
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      fetchErrorLogs({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      }),
    );
    // }
  }, [filters]);

  return (
    <div>
      <PageTitle title={t('websocketManagement')} />
      <CustomTable
        version="v2"
        header={column}
        data={devices}
        serialNo
        filter={{}}
        filterClearFunction={() => { }}
        filterFunction={() => { }}
        actionButton={false}
        limit={limit}
        page={page}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        pageLimitChange={(value: any) => pageNateLimitChange(value)}
        sizeChangeButtonRequired
        // rowClickEvent={handleClickTableRow}
        filters={filters}
        activeSearchFilter
        refreshRequired
        handleRefresh={handleRefresh}
        ReferenceCurrentPages={handleRefresh}
        hoverLabel="Refresh"
        filtersToDispatch={updateErrorLogsFilters}
        filterListLoading={false}
        filterListToDispatch={fetchErrorLogsFilters}
      // leftTopContent={
      //   <>
      //     <div>
      //       <CustomInput
      //         inputLabel=""
      //         type="text"
      //         placeholder={t('searchHere')}
      //         name="searchBy"
      //         svgIcon={<SearchIcon />}
      //         value={valueToShow}
      //         onChangeValue={(_, value) => handleChangeFilters('searchBy', String(value))}
      //       />
      //     </div>
      //     {leftTopContent}
      //   </>
      // }
      />
    </div>
  );
}

export default ErrorLogs;
